/**
 * iter234 · RAW MATERIAL INTELLIGENCE drawer (spec 21-24).
 *
 * For the selected production slot:
 *   RM · Required · Physical · Reserved · Net Available · Incoming · Short
 * plus the earliest feasible production date, the incoming deliveries behind
 * it (vendor · ETA · inward · QC) and a one-click draft purchase order for
 * whatever is genuinely short.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Truck, ShoppingCart, Lock, Unlock } from 'lucide-react';
import { fmtQty } from '../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LEVEL = {
  ready: { t: 'text-emerald-300', b: 'border-emerald-500/30 bg-emerald-500/10', l: '🟢 Ready — every material covered' },
  partial: { t: 'text-orange-300', b: 'border-orange-500/30 bg-orange-500/10', l: '🟡 Partial — some material short' },
  short: { t: 'text-red-300', b: 'border-red-500/30 bg-red-500/10', l: '🔴 Short — nothing on the way' },
  no_recipe: { t: 'text-zinc-400', b: 'border-zinc-700 bg-zinc-800/40', l: 'No recipe linked to this product' },
};

export default function RmDrawer({ planId, title, subtitle, onClose, onChanged }) {
  const [intel, setIntel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    if (!planId) return;
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/factory-plans/${planId}/rm-intelligence`, { withCredentials: true }),
        axios.get(`${API}/rm-reservations/plan/${planId}`, { withCredentials: true }),
      ]);
      setIntel(a.data);
      setDetail(b.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load RM intelligence'); }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const act = async (kind, materialId) => {
    setBusy(kind);
    try {
      if (kind === 'reserve') {
        const res = await axios.post(`${API}/rm-reservations/reserve/${planId}`, {}, { withCredentials: true });
        toast[res.data.at_risk ? 'warning' : 'success'](res.data.at_risk
          ? 'Reserved what exists · slot tagged ⚠ AT RISK'
          : `Reserved ${res.data.reservations.length} material(s)`);
      } else if (kind === 'release') {
        await axios.post(`${API}/rm-reservations/release/${planId}`, { reason: 'manual' }, { withCredentials: true });
        toast.success('Released back to available stock');
      } else if (kind === 'buy') {
        const res = await axios.post(`${API}/factory-plans/${planId}/purchase-requirement`,
          materialId ? { material_id: materialId } : {}, { withCredentials: true });
        res.data.success ? toast.success(res.data.message) : toast.info(res.data.message);
      }
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(''); }
  };

  const lvl = LEVEL[intel?.level] || LEVEL.no_recipe;
  const rows = intel?.materials || [];
  const shortRows = rows.filter(r => r.short > 0.001);
  const active = (detail?.reservations || []).filter(r => r.status === 'active');
  const slips = intel && intel.earliest_feasible_date && intel.plan?.planned_date
    && intel.earliest_feasible_date > intel.plan.planned_date;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end justify-end" onClick={onClose}>
      <div className="ti-surface-modal border-l border-zinc-800 w-full max-w-3xl h-full overflow-auto"
        onClick={e => e.stopPropagation()} data-testid="fp-rm-drawer">
        <div className="sticky top-0 ti-surface-modal border-b border-zinc-800 px-4 py-3 flex justify-between items-start z-10">
          <div>
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Raw material intelligence</p>
            <p className="font-heading text-sm text-zinc-100">{title}</p>
            <p className="text-[11px] text-zinc-400">
              {subtitle} · {planId}
              {intel?.plan ? ` · ${fmtQty(intel.plan.quantity, 'kg')} · ${intel.plan.batches} batch(es) · ${intel.plan.planned_date}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-100 text-lg">×</button>
        </div>

        <div className="p-4 space-y-3">
          <div className={`rounded-xl border p-3 ${lvl.b}`} data-testid="fp-rm-level">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className={`text-sm font-heading font-semibold ${lvl.t}`}>{lvl.l}</p>
              <p className="text-[11px] text-zinc-400">
                {intel ? `${intel.required_count} material(s) · ${intel.short_count} short` : '…'}
              </p>
            </div>
            {intel?.has_recipe && (
              <p className={`text-[11px] mt-1 ${(slips || !intel.earliest_feasible_date) ? 'text-orange-300' : 'text-zinc-400'}`} data-testid="fp-rm-earliest">
                Earliest feasible production date:{' '}
                <span className="font-mono">{intel.earliest_feasible_date || 'unknown'}</span>
                {' — '}{intel.earliest_note}
                {slips ? ` (after the planned ${intel.plan.planned_date}, so this slot slips)` : ''}
              </p>
            )}
            {intel?.blocked_by?.length > 0 && (
              <p className="text-[11px] text-red-300 mt-1">
                Blocked by (nothing incoming): {intel.blocked_by.join(', ')}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => act('reserve')} disabled={!!busy} data-testid="fp-rm-reserve-btn"
              className="bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black font-semibold text-xs px-3 py-2 rounded-lg flex items-center gap-1.5">
              <Lock size={12} /> {busy === 'reserve' ? '…' : active.length ? 'Re-reserve RM' : 'Reserve RM'}
            </button>
            {active.length > 0 && (
              <button onClick={() => act('release')} disabled={!!busy} data-testid="fp-rm-release-btn"
                className="ti-btn-ghost text-xs px-3 py-2 rounded-lg flex items-center gap-1.5">
                <Unlock size={12} /> Release
              </button>
            )}
            {shortRows.length > 0 && (
              <button onClick={() => act('buy')} disabled={!!busy} data-testid="fp-rm-buy-btn"
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5">
                <ShoppingCart size={12} /> {busy === 'buy' ? '…' : `Draft PO for ${shortRows.length} short material(s)`}
              </button>
            )}
          </div>

          {rows.length > 0 && (
            <div className="border border-zinc-800 rounded-xl overflow-hidden">
              <table className="w-full text-[11px]" data-testid="fp-rm-table">
                <thead className="bg-zinc-800/60">
                  <tr>
                    {['Raw material', 'Required', 'Physical', 'Reserved here', 'Reserved elsewhere',
                      'Net available', 'Incoming', 'Short', ''].map((h, i) => (
                      <th key={h + i} className={`px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-400 ${i === 0 ? 'text-left' : 'text-right'} ${i === 8 ? 'text-center' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m, i) => (
                    <tr key={i} className={`border-t border-zinc-800/60 ${m.short > 0.001 ? 'bg-red-500/[0.05]' : ''}`}
                      data-testid={`fp-rm-row-${m.material_id || i}`}>
                      <td className="px-2 py-1.5 text-zinc-100">
                        {m.material_name}
                        <span className="block text-[10px] text-zinc-500">
                          {fmtQty(m.per_batch)} {m.unit}/batch × {m.batches}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-zinc-200">{fmtQty(m.required_qty)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-zinc-400">{fmtQty(m.physical)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmtQty(m.reserved_here)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-zinc-500">{fmtQty(m.reserved_other)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-zinc-200">{fmtQty(m.net_available)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-blue-300">
                        {m.incoming_qty ? fmtQty(m.incoming_qty) : '—'}
                        {m.eta && <span className="block text-[10px] text-blue-400">ETA {m.eta}</span>}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-mono ${m.short > 0.001 ? 'text-red-300 font-semibold' : 'text-zinc-600'}`}>
                        {m.short > 0.001 ? fmtQty(m.short) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {m.short > 0.001 && (
                          <button onClick={() => act('buy', m.material_id)} disabled={!!busy}
                            title={`Buy ${fmtQty(m.recommended_purchase)} ${m.unit} · order by ${m.latest_order_date}`}
                            data-testid={`fp-rm-buy-${m.material_id}`}
                            className="text-blue-300 hover:text-blue-200 text-[10px] underline">
                            buy {fmtQty(m.recommended_purchase)}
                          </button>
                        )}
                        {m.short <= 0.001 && <CheckCircle2 size={12} className="text-emerald-500 inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows.some(m => (m.incoming || []).length > 0) && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase text-zinc-500 tracking-wider flex items-center gap-1">
                <Truck size={11} /> Incoming material
              </p>
              {rows.flatMap(m => (m.incoming || []).map((i, k) => (
                <div key={`${m.material_id}-${k}`} className="ti-surface-inset border border-zinc-800 rounded-lg px-2 py-1.5 text-[11px] flex flex-wrap gap-3"
                  data-testid={`fp-rm-incoming-${m.material_id}-${k}`}>
                  <span className="text-zinc-100">{m.material_name}</span>
                  <span className="text-zinc-400">{fmtQty(i.qty)} {i.unit}</span>
                  <span className="text-zinc-400">{i.vendor_name}</span>
                  <span className="text-blue-300">ETA {i.expected_date || '—'}</span>
                  <span className="text-zinc-500">PO {i.po_id} · {i.po_status}</span>
                  <span className="text-zinc-500">inward {i.inward_date || 'awaited'}</span>
                  <span className={i.qc_status === 'passed' ? 'text-emerald-300' : 'text-orange-300'}>QC {i.qc_status}</span>
                </div>
              )))}
            </div>
          )}

          {!intel?.has_recipe && (
            <div className="ti-callout-warn text-xs flex items-start gap-2">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              No recipe is linked to this product, so raw material cannot be calculated.
              Add the recipe in Recipes, or link the product to an existing one.
            </div>
          )}

          {(detail?.events || []).length > 0 && (
            <details className="pt-1">
              <summary className="text-[10px] uppercase text-zinc-500 tracking-wider cursor-pointer">
                Reservation audit ({detail.events.length})
              </summary>
              <div className="mt-2 space-y-1">
                {detail.events.slice(0, 30).map((e, i) => (
                  <div key={i} className="text-[11px] text-zinc-400">
                    <span className="text-zinc-500">{e.at?.slice(0, 16).replace('T', ' ')}</span> · {e.event} ·{' '}
                    <span className="text-zinc-200 font-mono">{fmtQty(e.delta_qty)}</span> · {e.user}
                    {e.note ? ` · ${e.note}` : ''}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
